import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js/min';
/**
 * Phone numbers as people read them, for any country. A number typed without
 * a country code is read as `DEFAULT_PHONE_COUNTRY`'s (the office is in the
 * US); one starting `+` is read as that country's. North American numbers are
 * shown the way the office writes them — `(305) 555-1234`, whether or not a
 * `+1` was typed — and every other country in its international form:
 * `+972 52 123 4567`, `+44 20 7946 0958`. A number that does not read as a
 * real one is left exactly as it was typed rather than guessed at.
 *
 * A number is stored as text; every reader that dials it (`toE164`, WhatsApp,
 * duplicate checks) works from its digits, so the layout is for the eye only.
 * The parsing is `libphonenumber-js` (its `min` metadata — small enough for the
 * browser bundle).
 */
export const DEFAULT_PHONE_COUNTRY = 'US';
/** Characters a number may be written with, an extension aside. */
const PHONE_CHARACTERS = /^[\d\s().+\-/]*$/;
function parse(value, country) {
    const text = value?.trim() ?? '';
    if (!text)
        return null;
    const number = parsePhoneNumberFromString(text, country);
    return number?.isValid() ? number : null;
}
/**
 * A phone number for a screen, a report or a printed page. A number this
 * cannot read is returned trimmed but otherwise as typed; nothing becomes `''`
 * except nothing. An extension (`x12`, `ext. 12`) is kept as `ext. 12`.
 */
export function formatPhone(value, country = DEFAULT_PHONE_COUNTRY) {
    const number = parse(value, country);
    if (!number) {
        const text = value?.trim() ?? '';
        // A seven-digit local number with no area code: `555-1234`.
        const local = PHONE_CHARACTERS.test(text) && !text.startsWith('+') && text.replace(/\D/g, '');
        return local && local.length === 7 ? `${local.slice(0, 3)}-${local.slice(3)}` : text;
    }
    return number.countryCallingCode === '1' ? number.formatNational() : number.formatInternational();
}
/**
 * A phone field's text while it is being typed, laid out digit by digit for
 * the country it is in (`+` and a code for another country). Digits are never
 * added or dropped — only the punctuation between them moves — which is what
 * lets a field keep the cursor after the same digit. Text carrying letters (an
 * extension) is left as typed.
 */
export function formatPhoneAsTyped(value, country = DEFAULT_PHONE_COUNTRY) {
    if (!PHONE_CHARACTERS.test(value))
        return value;
    if (!/\d/.test(value))
        return value.trimStart().startsWith('+') ? '+' : '';
    const plus = value.trimStart().startsWith('+');
    const digits = value.replace(/\D/g, '');
    // An area code on its own is not yet anything to lay out: `305`, not `(305)`.
    if (!plus && digits.length <= 3)
        return digits;
    // Never end on a separator, so a backspace always takes a digit.
    return new AsYouType(country).input(value).replace(/[^\d+]+$/, '');
}
/**
 * The number in E.164 (`+13055551234`) — what a dialler, SMS or WhatsApp
 * needs — or null when it does not read as a real number.
 */
export function phoneToE164(value, country = DEFAULT_PHONE_COUNTRY) {
    return parse(value, country)?.number ?? null;
}
/** `tel:` / `sms:` for a phone number however it is written. */
export function phoneHref(scheme, phone) {
    return `${scheme}:${phoneToE164(phone) ?? phone.replace(/[^\d+]/g, '')}`;
}
//# sourceMappingURL=phone.js.map